import firebase from "firebase/app";
import { StorageAdapter, StorageCallback } from '../types/storage/StorageAdapter';
import { Storage } from "../types/storage/common";

export class ClientStorageAdapter implements StorageAdapter {
    private storage: Storage;

    constructor(storage: Storage) {
        this.storage = storage;
    }

    private getRef(path: string): firebase.storage.Reference {
        return this.storage.ref(path);
    }

    upload(
        path: string,
        buffer: Blob | ArrayBuffer | Uint8Array<ArrayBufferLike>,
        callback?: StorageCallback,
        metadata?: { contentType?: string; customMetadata?: Record<string, string>; }
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const storageRef = this.getRef(path);
            const uploadTask = storageRef.put(buffer, metadata);
            uploadTask.on("state_changed",
                (snapshot) => {
                    const percent = Math.round(
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    );

                    // update progress
                    callback?.(percent);
                },
                (err) => {
                    callback?.(0, undefined, err);
                    reject(err)
                },
                () => {
                    // download url
                    storageRef.getDownloadURL().then((url) => {
                        callback?.(100, { path, name: storageRef.name, url });
                    });
                    resolve();
                })
        })
    }

    async delete(path: string): Promise<void> {
        await this.getRef(path).delete();
    }

    async exists(path: string): Promise<boolean> {
        try {
            await this.getRef(path).getMetadata();
            return true;
        } catch (error: any) {
            if (error.code === 'storage/object-not-found') {
                return false;
            }
            throw error;
        }
    }

    async getDownloadUrl(path: string): Promise<string> {
        return await this.getRef(path).getDownloadURL();
    }

    async download(path: string): Promise<Uint8Array> {
        // const fileRef = this.getRef(path);
        // Get as Blob (Client SDK method for v10)
        // const blob = await getBlob(fileRef);
        // Convert to Uint8Array
        // const arrayBuffer = await blob.arrayBuffer();
        // return new Uint8Array(arrayBuffer);

        const url = await this.getDownloadUrl(path);
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        return new Uint8Array(arrayBuffer);
        // var xhr = new XMLHttpRequest();
        // xhr.responseType = 'blob';
        // xhr.onload = (event) => {
        //     // eslint-disable-next-line @typescript-eslint/no-unused-vars
        //     var blob = xhr.response;
        // };
        // xhr.open('GET', url);
        // xhr.send();
    }

    async getMetadata(path: string): Promise<{
        size: number;
        contentType: string;
        updated: string;
        [key: string]: any;
    }> {
        const metadata = await this.getRef(path).getMetadata();

        return {
            size: metadata.size,
            contentType: metadata.contentType || 'application/octet-stream',
            updated: metadata.updated,
            ...metadata,
        };
    }

    async updateMetadata(
        path: string,
        metadata: Record<string, any>
    ): Promise<void> {
        await this.getRef(path).updateMetadata({ customMetadata: metadata });
    }

    async copy(sourcePath: string, destinationPath: string): Promise<void> {
        // Download source
        const sourceData = await this.download(sourcePath);
        const sourceMetadata = await this.getMetadata(sourcePath);

        // Re-upload to destination
        await this.upload(destinationPath, sourceData, undefined, {
            contentType: sourceMetadata.contentType,
            customMetadata: sourceMetadata.customMetadata,
        });
    }

    async move(sourcePath: string, destinationPath: string): Promise<void> {
        await this.copy(sourcePath, destinationPath);
        await this.delete(sourcePath);
    }

    async listFiles(prefix: string): Promise<{ path: string; url: any; name: string; }[]> {
        const result = await this.getRef(prefix).listAll();
        return Promise.all(result.items.map(async item => ({ path: item.fullPath, url: await item.getDownloadURL(), name: item.name })));
    }

    async batchDelete(paths: string[]): Promise<void> {
        await Promise.all(paths.map(path => this.delete(path)));
    }

    /**
       * Make file public
       * Note: Firebase Client SDK doesn't support this directly
       * You'll need to configure Firebase Storage Rules or use Admin SDK
       */
    async makePublic(path: string): Promise<string> {
        // Client SDK can't make files public directly
        // This requires either:
        // 1. Storage security rules that allow public read
        // 2. Admin SDK on backend

        // For now, return the download URL
        // To truly make public, configure Storage Rules:
        // allow read: if true;

        console.warn(
            'ClientStorageAdapter.makePublic(): ' +
            'Cannot make files public from client SDK. ' +
            'Configure Storage Rules or use Admin SDK on backend.'
        );

        return await this.getDownloadUrl(path);
    }

    /**
     * Make file private
     * Note: Firebase Client SDK doesn't support this directly
     */
    async makePrivate(path: string): Promise<void> {
        // Client SDK can't change file permissions directly
        // This requires either:
        // 1. Storage security rules
        // 2. Admin SDK on backend

        console.warn(
            'ClientStorageAdapter.makePrivate(): ' +
            'Cannot make files private from client SDK. ' +
            'Configure Storage Rules or use Admin SDK on backend.'
        );
    }
}